import React, { useState, useEffect } from "react";
import { getPageStyles } from "../../SiteManagement/styles"; //TODO export these further up!
import {
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ssoManagementService from "../../../services/SSOManagementService";
import { LTIPlatformDTO } from "@beable/roster-backend-client-v1";
import { SUBPAGES } from "../constants";
import Paging from "../Common/Paging";

interface SSOLTIPlatformProps {
  action: "search" | "refresh" | undefined;
  selectedItem?: LTIPlatformDTO;
  setSelectedItem: (item: LTIPlatformDTO) => void;
  searchTerm: string;
}

export default function LTIPlatformList({
  action,
  selectedItem,
  setSelectedItem,
  searchTerm,
}: SSOLTIPlatformProps) {
  const theme = useTheme();
  const styles = getPageStyles(theme);
  const defaultState = SUBPAGES.LTIProviders.defaultState;

  const [fetchData, setFetchData] = useState(true);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [LTIProvidersListItems, setLTIProvidersListItems] = useState<
    LTIPlatformDTO[]
  >([]);
  const [searchLTIProvidersResult, setSearchLTIProvidersResult] = useState<
    LTIPlatformDTO[]
  >([]);
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [isLoading, setIsloading] = useState(false);

  const handlePaging = (newPage: number) => {
    setPage(newPage);
    setFetchData(true);
  };

  useEffect(() => {
    const handleSearch = async () => {
      setNoSearchResults(false);
      setIsloading(true);

      if (searchTerm !== "") {
        const searchResult = await ssoManagementService.searchLTIPlatforms(
          searchTerm
        );
        if (searchResult && searchResult.data.length === 0) {
          setNoSearchResults(true);
        }
        setSearchLTIProvidersResult(searchResult?.data ?? []);
      } else {
        setSearchLTIProvidersResult([]);
      }
      setIsloading(false);
    };

    if (action === "refresh") {
      setSearchLTIProvidersResult([]);
      setFetchData(true);
    }

    if (action === "search") {
      handleSearch();
    }
  }, [action, searchTerm]);

  const handleTableRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.persist();
    e.stopPropagation();

    const selectedRow = e.currentTarget;
    const searchIsEmpty = searchTerm === "";
    const match = searchIsEmpty
      ? LTIProvidersListItems.find(
          (item) => item.id?.toString() === selectedRow.id
        )
      : searchLTIProvidersResult.find(
          (item) => item.id?.toString() === selectedRow.id
        );

    if (match && match.id !== selectedItem?.id) {
      setSelectedItem(match);
    } else {
      setSelectedItem(defaultState);
    }
  };

  useEffect(() => {
    const getLTIProviders = async () => {
      setIsloading(true);
      const LTIProvidersResponse = await ssoManagementService.getLTIPlatforms(
        page
      );
      if (LTIProvidersResponse) {
        setLTIProvidersListItems(LTIProvidersResponse.data);
        setPageCount(LTIProvidersResponse.pages);
      }
      setIsloading(false);
    };

    if (fetchData) {
      getLTIProviders();
      setFetchData(false);
    }
  }, [fetchData, page]);

  const getListItems = () => {
    if (searchLTIProvidersResult.length) {
      return searchLTIProvidersResult;
    }

    return LTIProvidersListItems;
  };

  return (
    <Container sx={styles.dfb}>
      {noSearchResults ? (
        <Typography sx={styles.mb2}>
          No search results found.
        </Typography>
      ) : undefined}
      <Paging
        filteredResultsLength={searchLTIProvidersResult.length}
        totalPages={pageCount}
        setPage={handlePaging}
      />
      {isLoading ? (
        <CircularProgress color="primary" />
      ) : (
        <TableContainer component={Paper} sx={styles.maxHeight}>
          <Table
            sx={styles.table}
            aria-label="LTIProviders data table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">Environment</TableCell>
                <TableCell align="left">Issuer</TableCell>
                <TableCell align="left">Auth&nbsp;Redirect&nbsp;URL</TableCell>
                <TableCell align="left">UserLookupField</TableCell>
                <TableCell align="left">Client&nbsp;ID</TableCell>
                <TableCell align="left">Deployment&nbsp;ID</TableCell>
                <TableCell align="left">Keyset</TableCell>
                <TableCell align="left">Keyset&nbsp;URL</TableCell>
                <TableCell align="left">Site&nbsp;ID</TableCell>
                <TableCell align="left">LogoutRedirectUrl</TableCell>
                <TableCell align="left">IdentifierTransforms</TableCell>
                <TableCell align="left">Static&nbsp;Launch</TableCell>
                <TableCell align="left">Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getListItems()?.map((item) => (
                <TableRow
                  sx={
                    selectedItem?.id &&
                    selectedItem?.id.toString() === item.id?.toString()
                      ? styles.highlight
                      : undefined
                  }
                  id={item?.id?.toString()}
                  key={item.id}
                  onClick={handleTableRowClick}
                >
                  <TableCell align="left">{item.name}</TableCell>
                  <TableCell align="left">{item.environment}</TableCell>
                  <TableCell align="left">{item.issuer}</TableCell>
                  <TableCell align="left">{item.authRedirectUrl}</TableCell>
                  <TableCell align="left">{item.userLookupField}</TableCell>
                  <TableCell align="left">{item.clientId}</TableCell>
                  <TableCell align="left">{item.deploymentId}</TableCell>
                  <TableCell align="left">{item.keyset}</TableCell>
                  <TableCell align="left">{item.keysetUrl}</TableCell>
                  <TableCell align="left">{item.siteId}</TableCell>
                  <TableCell align="left">{item.logoutRedirectUrl}</TableCell>
                  <TableCell align="left">{item.identifierTransforms}</TableCell>
                  <TableCell align="left">
                    {item.useStaticLaunch ? "true" : "false"}
                  </TableCell>
                  <TableCell align="left">
                    {item.active ? "true" : "false"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
